// Copyright ©️ 2026 eVolve MEP, LLC

/**
 * The entry point of the whole measurement. {@link measureRacks} reads as the pipeline itself: sort the
 * selection into supports and conduit, measure a cross-section at every support, drop the repeats.
 *
 * The support is the unit of measurement, not the rack — a run is dimensioned wherever a fabricator reads
 * it, so one conduit appears in several cross-sections. Anything unmeasurable drops out rather than
 * throwing, which makes an empty result the normal answer for a selection holding no rack.
 *
 * `RackDimensions/README.md` covers the reasoning behind each stage.
 */

import {
  buildRackElementCandidates,
  type RackElementCandidates,
} from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/candidatePreprocessing/buildRackElementCandidates';
import {
  buildMeasuredCrossSection,
  type MeasuredCrossSection,
} from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/crossSectionPreprocessing/buildMeasuredCrossSection';
import {
  buildConduitRuns,
  type ConduitRunId,
} from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/crossSectionPreprocessing/groupRackElements';
import { resolveRackFramePoint } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/crossSectionPreprocessing/rackFrame';
import { boxesIntersect } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/geometryHelpers';
import { formatSpanFt } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/rendering/formatSpanFt';
import type { RackPipe } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/types';
import { getFragmentList, getObjectTree } from 'components/Autodesk/extensions/viewerHelpers';
import type { AutodeskDbId } from 'components/Autodesk/types';
import { THREE } from 'components/Autodesk/viewerThree';
import { isNil, isNotNil } from 'helpers/isNotNil';

/** How far past its bounding box a support reaches for conduit: clamps, standoffs, a box's wall. */
const SUPPORT_REACH_MARGIN_FT = 0.5;

/**
 * The conduits worth measuring against one support: those whose world bounds reach it. Gates the expensive
 * work, since recovering a centerline can mean fitting a ray against every vertex of an element's mesh.
 */
const findPipesNearSupport = (
  supportBounds: THREE.Box3,
  { conduitWorldBounds, readConduitPipes }: RackElementCandidates,
): RackPipe[] => {
  const reach = supportBounds.clone().expandByScalar(SUPPORT_REACH_MARGIN_FT);
  return [...conduitWorldBounds.entries()]
    .filter(([, bounds]) => boxesIntersect(reach, bounds))
    .flatMap(([dbId]) => readConduitPipes(dbId));
};

/** Where a cross-section's chain sits in world space. */
const anchorOf = ({ frame, dimensionStationFt }: MeasuredCrossSection): THREE.Vector3 =>
  resolveRackFramePoint(frame, { widthFt: 0, heightFt: 0, stationFt: dimensionStationFt });

/** The physical runs a cross-section measures, however many elements those runs are built from. */
const runSetKeyOf = (
  { tiers }: MeasuredCrossSection,
  runIdOf: (pipe: RackPipe) => ConduitRunId | null,
): string | null => {
  const runIds = tiers.flatMap(({ pipes }) => pipes.map(runIdOf));
  const tracedRunIds = runIds.filter(isNotNil);
  // One untraced conduit leaves the whole set unknown.
  if (tracedRunIds.length !== runIds.length) return null;
  return [...new Set(tracedRunIds)].toSorted((a, b) => a - b).join(',');
};

/** What a cross-section reports, as the drawing reads it. 2 chains reading alike are one measurement. */
const chainKeyOf = ({ tiers }: MeasuredCrossSection): string =>
  tiers.map(({ chainFt }) => chainFt.map(formatSpanFt).join(',')).join('|');

/**
 * Of every set of cross-sections measuring the same runs and reporting the same numbers, keep the most
 * exterior — the end a fabricator reads from. Grouping on run *and* chain is what lets a spacing change
 * part-way along a run keep its own cross-section.
 */
const cullRepeatedChains = (
  crossSections: MeasuredCrossSection[],
  runIdOf: (pipe: RackPipe) => ConduitRunId | null,
  spoolCenter: THREE.Vector3,
): MeasuredCrossSection[] => {
  const kept: MeasuredCrossSection[] = [];
  const mostExteriorByGroup = new Map<string, MeasuredCrossSection>();

  for (const crossSection of crossSections) {
    const runSetKey = runSetKeyOf(crossSection, runIdOf);
    // An unknown run must not match anything.
    if (isNil(runSetKey)) {
      kept.push(crossSection);
      continue;
    }

    const groupKey = `${runSetKey}|${chainKeyOf(crossSection)}`;
    const rival = mostExteriorByGroup.get(groupKey);
    const isMoreExterior =
      isNil(rival) || anchorOf(crossSection).distanceTo(spoolCenter) > anchorOf(rival).distanceTo(spoolCenter);
    if (isMoreExterior) mostExteriorByGroup.set(groupKey, crossSection);
  }

  return [...kept, ...mostExteriorByGroup.values()];
};

/** Measure every conduit rack among `dbIds`. Empty when the selection holds no rack this feature supports. */
export const measureRacks = async (
  viewer: Autodesk.Viewing.GuiViewer3D,
  dbIds: Set<AutodeskDbId>,
): Promise<MeasuredCrossSection[]> => {
  const model = viewer.model;
  // `model` is momentarily undefined while a model swaps, and a single element cannot define spacing.
  if (isNil(model) || dbIds.size < 2) return [];

  const candidates = await buildRackElementCandidates(model, [...dbIds]);

  const instanceTree = await getObjectTree(model);
  const fragList = getFragmentList(model);

  const crossSections = [...candidates.supportWorldBounds.entries()]
    .map(([supportDbId, supportBounds]) =>
      buildMeasuredCrossSection(instanceTree, fragList, supportDbId, findPipesNearSupport(supportBounds, candidates)),
    )
    .filter(isNotNil);

  return cullRepeatedChains(
    crossSections,
    buildConduitRuns(candidates.tracedConduitPipes()),
    candidates.selectionWorldBounds.getCenter(new THREE.Vector3()),
  );
};
